import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  emailAddressFromPatient,
  phoneNumberFromPatient,
  phoneNumberTypePatient,
} from '@app/domain/patient/patient.helpers'
import type { IProgramEnrolment } from '@app/domain/program/program.interface'
import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'
import type {
  IApiUpdatePatient,
  IApiUpdateContactInformation,
} from '@app/state/patients/helpers'
import { convertApiPatient } from '@app/state/patients/helpers'
import type {
  AddContactInformationResponse,
  UpdatePatientResponse,
  UpdateContactInformationResponse,
} from '@app/state/patients/rest'
import {
  addContactInformationToPatient,
  updatePatient,
  updatePatientContactInformation,
} from '@app/state/patients/rest'

import {
  getPatient,
  getPatients,
  getProgramEnrolments,
  revokeRemoteInteraction,
  sendInteractionToIntakeAnywhere,
  uploadPatientImportFile,
  getPatientCommunicationConsent,
  updatePatientEmailConsent,
  removePatientSmsConsent,
} from './patient.rest'

import type {
  IPatient,
  IPatientId,
  IPatientEmailAddress,
  IPatientPhoneNumber,
  IUseGetPatient,
  IUsePatient,
  IUsePatientSearch,
  IGetPatientOptions,
  IUseProgramEnrolments,
  PhoneNumberTypeId,
  IUseGetPatientStatus,
  IPatientCommunicationConsent,
} from './patient.interface'
import type { IPatientImportError } from './patient.rest'

export function usePatient(patientId?: IPatientId): IUsePatient {
  const [patient, setPatient] = useState<IPatient>()

  const requestor = useAuthenticatedRequestor()

  const getPatientFromServer = useCallback(async () => {
    const response = await requestor(getPatient(patientId))

    if (response.status === 200) {
      const patient = convertApiPatient(response.data.patient)
      setPatient(patient)
    } else {
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
  }, [patientId, requestor])

  const updatePatientOnServer = useCallback(
    async (patient: IApiUpdatePatient) => {
      console.log('update patient on server')
      const response = await requestor(updatePatient(patientId, patient))
      if (response.status === 200) {
        const patient = convertApiPatient(response.data.patient)
        setPatient(patient)
      } else {
        console.log('failure')
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    },
    [patientId, requestor],
  )

  useEffect(() => {
    if (patientId) {
      getPatientFromServer()
    }
  }, [getPatientFromServer, patientId])

  return {
    patient,
    getPatient: getPatientFromServer,
    updatePatient: updatePatientOnServer,
  }
}

export function useGetPatient(): IUseGetPatient {
  const [status, setStatus] = useState<IUseGetPatientStatus>('idle')
  const [patient, setPatient] = useState<IPatient>()

  const requestor = useAuthenticatedRequestor()

  const fetchPatient = useCallback(
    async (patientId: string) => {
      setStatus('loading')
      const response = await requestor(getPatient(patientId))

      if (response.status === 200) {
        const patient = convertApiPatient(response.data.patient)
        setPatient(patient)
        setStatus('success')
      } else {
        setStatus('error')
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    },
    [requestor],
  )

  return {
    patient,
    getPatient: fetchPatient,
    status,
  }
}

export function usePatientsSearch(
  defaultSearchOptions?: Partial<IGetPatientOptions>,
): IUsePatientSearch {
  const [patients, setPatients] = useState<IPatient[]>([])
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalNumberOfRecords, setTotalNumberOfRecords] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const requestor = useAuthenticatedRequestor()

  const fetchData = useCallback(
    async (pageNumber, recordsPerPage, options: IGetPatientOptions) => {
      const searchOptions = {
        ...defaultSearchOptions,
        ...options,
      }

      setIsLoading(true)
      const response = await requestor(
        getPatients(pageNumber, recordsPerPage, searchOptions),
      )

      if (response.status === 200) {
        setPatients(response.data.patients)
        setPageCount(response.data.pageCount)
        setTotalNumberOfRecords(response.data.totalNumberOfRecords)
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }

      setIsLoading(false)
    },
    [defaultSearchOptions, requestor],
  )

  return {
    patients,
    isLoading,
    pageCount: pageCount,
    totalNumberOfRecords: totalNumberOfRecords,
    getPatients: fetchData,
  }
}

interface IUpdatePatient {
  updatePatient: (
    id: IPatientId,
    patient: IApiUpdatePatient,
  ) => Promise<UpdatePatientResponse>
}

interface IUpdatePatientContactInformation {
  updatePatientContactInformation: (
    patientId: string,
    contactInformation: IApiUpdateContactInformation,
  ) => Promise<UpdateContactInformationResponse>
}

export function useUpdatePatient(): IUpdatePatient {
  const requestor = useAuthenticatedRequestor()

  const sendRequestToServer = useMemo(() => {
    return async (patientId: IPatientId, patient: IApiUpdatePatient) => {
      const response = await requestor(updatePatient(patientId, patient))
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    }
  }, [requestor])

  return {
    updatePatient: sendRequestToServer,
  }
}

export function useUpdatePatientContactInformation(): IUpdatePatientContactInformation {
  const requestor = useAuthenticatedRequestor()
  const sendRequestToServer = useMemo(() => {
    return async (
      patientId: string,
      contactInformation: IApiUpdateContactInformation,
    ) => {
      const response = await requestor(
        updatePatientContactInformation(
          patientId,
          emailAddressFromPatient(contactInformation),
          phoneNumberFromPatient(contactInformation),
          phoneNumberTypePatient(contactInformation),
        ),
      )
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    }
  }, [requestor])

  return {
    updatePatientContactInformation: sendRequestToServer,
  }
}

export function useProgramEnrolments(): IUseProgramEnrolments {
  const [patientProgramEnrolments, setPatientProgramEnrolments] = useState<
    IProgramEnrolment[]
  >()

  const requestor = useAuthenticatedRequestor()

  const fetchPatientProgramEnrolments = useCallback(
    async (patientId: string) => {
      const response = await requestor(getProgramEnrolments(patientId))

      if (response.status === 200) {
        const patientProgramEnrolments = response.data.patientEnrolments
        setPatientProgramEnrolments(patientProgramEnrolments)
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    },
    [requestor],
  )

  const handleRevokeInteraction = useCallback(
    async (patientId: string, programInteractionId: string) => {
      const response = await requestor(
        revokeRemoteInteraction(patientId, programInteractionId),
      )
      if (response.status === 200) {
        fetchPatientProgramEnrolments(patientId)
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    },
    [fetchPatientProgramEnrolments, requestor],
  )

  const handleSendInteractionToIntakeAnywhere = useCallback(
    async (patientId: string, programInteractionId: string) => {
      const response = await requestor(
        sendInteractionToIntakeAnywhere(patientId, programInteractionId),
      )
      if (response.status === 200) {
        fetchPatientProgramEnrolments(patientId)
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    },
    [fetchPatientProgramEnrolments, requestor],
  )

  return {
    patientProgramEnrolments,
    revokeInteraction: handleRevokeInteraction,
    getPatientProgramEnrolments: fetchPatientProgramEnrolments,
    sendInteractionToIntakeAnywhere: handleSendInteractionToIntakeAnywhere,
  }
}

interface IAddPatientContactInformation {
  addContactInformationToPatient: (
    patientId: IPatientId,
    email: IPatientEmailAddress,
    phoneNumber: IPatientPhoneNumber,
    phoneType: PhoneNumberTypeId,
  ) => Promise<AddContactInformationResponse>
}

export function useAddPatientContactInformation(): IAddPatientContactInformation {
  const requestor = useAuthenticatedRequestor()
  const addPatientContactInformation = useCallback(
    async (
      patientId: IPatientId,
      email: IPatientEmailAddress,
      phoneNumber: IPatientPhoneNumber,
      phoneType: PhoneNumberTypeId,
    ) => {
      const response = await requestor(
        addContactInformationToPatient(
          patientId,
          email,
          phoneNumber,
          phoneType,
        ),
      )
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    },
    [requestor],
  )

  return {
    addContactInformationToPatient: addPatientContactInformation,
  }
}

interface IUseBulkPatientImport {
  clearStatus: () => void
  isLoading: boolean
  numberAdded?: number
  importErrors: IPatientImportError[]
  importStatus?: 'success' | 'failure'
  numberOfPatientsAddedToPrograms: Record<string, number>
  stage?: 'validation' | 'import'
  uploadError?: string
  uploadStatus?: 'success' | 'failure'
  uploadPatients: (file: File, selectedPrograms: string[]) => void
}

export function useBulkPatientImport(): IUseBulkPatientImport {
  const requestor = useAuthenticatedRequestor()
  const [isLoading, setIsLoading] = useState(false)
  const [numberAdded, setNumberAdded] = useState<number>(undefined)
  const [
    numberOfPatientsAddedToPrograms,
    setNumberOfPatientsAddedToPrograms,
  ] = useState<Record<string, number>>({})
  const [importErrors, setImportErrors] = useState<IPatientImportError[]>([])
  const [stage, setStage] = useState<'validation' | 'import' | undefined>(
    undefined,
  )

  const [importStatus, setImportStatus] = useState<
    'success' | 'failure' | undefined
  >(undefined)

  const [uploadStatus, setUploadStatus] = useState<
    'success' | 'failure' | undefined
  >(undefined)

  const [uploadError, setUploadError] = useState<string | undefined>(undefined)

  const clearStatus = useCallback(() => {
    setIsLoading(false)
    setNumberAdded(undefined)
    setImportErrors([])
    setStage(undefined)
    setImportStatus(undefined)
    setUploadError(undefined)
    setUploadStatus(undefined)
    setNumberOfPatientsAddedToPrograms({})
  }, [])

  const uploadPatients = useCallback(
    async (file: File, selectedPrograms: string[]) => {
      setIsLoading(true)
      setNumberAdded(undefined)
      setImportErrors([])
      setStage(undefined)
      setImportStatus(undefined)
      setUploadError(undefined)
      setUploadStatus(undefined)

      const request = uploadPatientImportFile(file, selectedPrograms)
      await requestor(request).then(
        (response) => {
          if (response.status === 200) {
            setIsLoading(false)
            setNumberAdded(response.data.numberAdded)
            setImportErrors(response.data.errors)
            setStage(response.data.stage)
            setImportStatus(response.data.status)
            setNumberOfPatientsAddedToPrograms(
              response.data.numberOfPatientsAddedToPrograms,
            )
            setUploadStatus('success')
          } else {
            setIsLoading(false)
            throw new Error(
              `server returned returned a non 200 response '${response.status}'`,
            )
          }
        },
        () => {
          setIsLoading(false)
          setUploadStatus('failure')
          setUploadError(
            'An error occurred while uploading the file. Please re-select the file and try again.',
          )
        },
      )
    },
    [requestor],
  )

  return {
    clearStatus,
    isLoading,
    importErrors,
    importStatus,
    numberAdded,
    numberOfPatientsAddedToPrograms,
    stage,
    uploadPatients,
    uploadError,
    uploadStatus,
  }
}

interface IUsePatientCommunicationConsent {
  communicationConsent: IPatientCommunicationConsent | null
  isLoading: boolean
  error: string | null
  getCommunicationConsent: (patientId: string) => Promise<void>
  updateEmailConsent: (
    patientId: string,
    consentsToEmail: boolean,
  ) => Promise<void>
  removeSmsConsent: (patientId: string) => Promise<void>
}

export function usePatientCommunicationConsent(): IUsePatientCommunicationConsent {
  const [
    communicationConsent,
    setCommunicationConsent,
  ] = useState<IPatientCommunicationConsent | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestor = useAuthenticatedRequestor()

  const fetchCommunicationConsent = useCallback(
    async (patientId: string) => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await requestor(
          getPatientCommunicationConsent(patientId),
        )

        if (response.status === 200) {
          setCommunicationConsent(response.data.communicationConsent)
        } else {
          setError(`Server returned a non-200 response '${response.status}'`)
        }
      } catch (error) {
        setError('Failed to fetch communication consent')
        console.error('Error fetching communication consent:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [requestor],
  )

  const handleUpdateEmailConsent = useCallback(
    async (patientId: string, consentsToEmail: boolean) => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await requestor(
          updatePatientEmailConsent(patientId, consentsToEmail),
        )

        if (response.status === 200) {
          setCommunicationConsent(response.data.communicationConsent)
        } else {
          setError(`Server returned a non-200 response '${response.status}'`)
        }
      } catch (error) {
        setError('Failed to update email consent')
        console.error('Error updating email consent:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [requestor],
  )

  const handleRemoveSmsConsent = useCallback(
    async (patientId: string) => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await requestor(removePatientSmsConsent(patientId))

        if (response.status === 200) {
          setCommunicationConsent(response.data.communicationConsent)
        } else {
          setError(`Server returned a non-200 response '${response.status}'`)
        }
      } catch (err) {
        setError('Failed to remove SMS consent')
        console.error('Error removing SMS consent:', err)
      } finally {
        setIsLoading(false)
      }
    },
    [requestor],
  )

  return {
    communicationConsent,
    isLoading,
    error,
    getCommunicationConsent: fetchCommunicationConsent,
    updateEmailConsent: handleUpdateEmailConsent,
    removeSmsConsent: handleRemoveSmsConsent,
  }
}
